import useStyles from 'isomorphic-style-loader/useStyles';
import { PanelRightClose, PanelRightOpen } from 'lucide-react';
import type { ReactNode } from 'react';

import s from './row-detail-panel-trigger.scss';

export interface RowDetailPanelTriggerProps {
  /**
   * The button's accessible name, kept the same while the panel is open so that
   * `aria-expanded` is what carries that state.
   */
  label: string;
  /**
   * Opens the panel on this row, or closes it when it is already open.
   */
  onToggle: () => void;
  /**
   * Rendered ahead of the button, e.g. the count the panel breaks down.
   */
  children?: ReactNode;
  /**
   * Whether the panel is currently open on this row.
   */
  expanded?: boolean;
}

/**
 * In-row control that opens a `useRowDetailPanel` panel on its row. Table rows
 * take no tab stop of their own, so this is also the keyboard path to the panel.
 */
export const RowDetailPanelTrigger = ({
  label,
  onToggle,
  children,
  expanded = false,
}: RowDetailPanelTriggerProps) => {
  useStyles(s);

  const Icon = expanded ? PanelRightClose : PanelRightOpen;

  return (
    <span className={s.root}>
      {children}

      <button
        className={s.trigger}
        aria-expanded={expanded}
        aria-label={label}
        onClick={onToggle}
        type="button"
      >
        <Icon />
      </button>
    </span>
  );
};

export default RowDetailPanelTrigger;
